/**
 * "My Attendance" — self-service view for any user with the
 * `can_self_mark_attendance` permission.
 *
 * UX:
 *  - Today card: clicking a status (Present / Half-day / Absent / Week-off)
 *    *stages* a draft change. It is NOT committed until the user clicks
 *    "Save changes" in the sticky bottom bar.
 *  - OT entry: pick a date, enter hours, then either tap one of the
 *    common reason chips (sourced from /api/payroll/settings) OR type
 *    a free-text reason. "Add" pushes it to a "pending OT entries" list.
 *  - The sticky bottom bar shows the total pending count and lets the
 *    user Save everything in one go, or Discard all drafts.
 *
 * This mirrors the draft-mode pattern used on the admin Staff page
 * (see StaffPage.jsx) so the experience is consistent.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarDays, CheckCircle2, XCircle, MinusCircle, Sun, Loader2,
  PlusCircle, Trash2, Save, Undo2, AlertCircle, FileDown, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';

const STATUS_OPTIONS = [
  { key: 'present',  label: 'Present',  icon: CheckCircle2, color: 'bg-emerald-500 text-white' },
  { key: 'half_day', label: 'Half day', icon: MinusCircle,  color: 'bg-amber-500 text-white' },
  { key: 'absent',   label: 'Absent',   icon: XCircle,      color: 'bg-red-500 text-white' },
  { key: 'week_off', label: 'Week off', icon: Sun,          color: 'bg-slate-400 text-white' },
];

const STATUS_DOT = {
  present:  'bg-emerald-500',
  half_day: 'bg-amber-500',
  absent:   'bg-red-500',
  week_off: 'bg-slate-300',
};

const DEFAULT_REASONS = [
  'Customer urgent delivery',
  'Late job finish',
  'Extra car / pre-booking',
  'Supplier / vendor delay',
  'Material prep',
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
const monthEnd = (d) => {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};
const monthLabel = (d) => d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
const monthValue = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const MyAttendancePage = () => {
  const { authHeader, user } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [attendance, setAttendance] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasonPresets, setReasonPresets] = useState(DEFAULT_REASONS);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingPayslip, setDownloadingPayslip] = useState(false);
  // { date_from, date_to, active } — admin-controlled grace period during
  // which the team can backfill past attendance and OT.
  const [backfill, setBackfill] = useState({ active: false, date_from: '', date_to: '' });

  // ── Draft state (NOT committed until "Save changes" clicked) ──
  // {date: status} for queued attendance changes (typically just today)
  const [attendanceDraft, setAttendanceDraft] = useState({});
  // Queue of OT entries to add. Each: {tmpId, date, hours, reason}
  const [otDraft, setOtDraft] = useState([]);

  // OT form (not yet added to draft)
  const [otForm, setOtForm] = useState({ date: todayStr(), hours: '', reason: '' });

  const from = useMemo(() => monthStart(cursor), [cursor]);
  const to = useMemo(() => monthEnd(cursor), [cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      const [a, o] = await Promise.all([
        axios.get(`${API}/attendance?${params}`, { headers: authHeader() }),
        axios.get(`${API}/overtime?${params}`, { headers: authHeader() }),
      ]);
      setAttendance(a.data || []);
      setOvertime((o.data || []).slice().sort((x, y) => (y.date || '').localeCompare(x.date || '')));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, from, to]);

  useEffect(() => { load(); }, [load]);

  // Fetch the active backfill window (separately because it doesn't depend
  // on the month cursor — same window applies to any month).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/attendance/backfill-window`, { headers: authHeader() });
        if (alive) setBackfill({
          active: !!data?.active,
          date_from: data?.date_from || '',
          date_to: data?.date_to || '',
        });
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [authHeader]);

  // Helper: is a given date editable by THIS staff right now?
  const isDateEditable = useCallback((ds) => {
    if (!ds) return false;
    if (ds === todayStr()) return true;
    if (ds > todayStr()) return false;
    if (!backfill.active) return false;
    if (backfill.date_from && ds < backfill.date_from) return false;
    if (backfill.date_to && ds > backfill.date_to) return false;
    return true;
  }, [backfill]);

  // Fetch the common OT reason presets configured by the admin (payroll
  // settings are admin-only, so skip the call for plain staff logins).
  useEffect(() => {
    let alive = true;
    if (!(user?.permissions || {}).can_view_payroll && !user?.is_master_admin) return undefined;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payroll/settings`, { headers: authHeader() });
        if (alive && Array.isArray(data?.ot_reason_presets) && data.ot_reason_presets.length > 0) {
          setReasonPresets(data.ot_reason_presets);
        }
      } catch { /* fall back to defaults; non-fatal */ }
    })();
    return () => { alive = false; };
  }, [authHeader, user]);

  // Build calendar grid
  const grid = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const lead = first.getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const cells = Array(lead).fill(null);
    const byDate = Object.fromEntries(attendance.map((a) => [a.date, a]));
    for (let d = 1; d <= total; d += 1) {
      const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ ds, day: d, rec: byDate[ds] || null });
    }
    return cells;
  }, [cursor, attendance]);

  const today = todayStr();
  const todayRec = attendance.find((a) => a.date === today);
  const todayDraftStatus = attendanceDraft[today];

  // ── Draft helpers ──
  // Clicking a status:
  //  - if the same status is already in DRAFT → clear the draft (toggle off)
  //  - otherwise → stage the new draft.
  // Importantly, re-clicking the status that is already SAVED (currentRec.status)
  // and not in draft does NOTHING (instead of silently discarding the draft),
  // so users always get visible feedback for their clicks.
  const stageAttendance = (ds, status) => {
    if (!isDateEditable(ds)) {
      toast.warning('That date is locked. Backfill window is closed.');
      return;
    }
    const draftStatus = attendanceDraft[ds];
    const savedRec = attendance.find((a) => a.date === ds);
    if (status === draftStatus) {
      // toggle the active draft off
      setAttendanceDraft((d) => {
        const { [ds]: _drop, ...rest } = d;
        return rest;
      });
      toast.message('Draft cleared. No change to save.');
      return;
    }
    if (!draftStatus && status === savedRec?.status) {
      // already saved as this — give the user explicit feedback instead of failing silent
      toast.info(`Already marked as ${status.replace('_', ' ').toUpperCase()} for ${ds === today ? 'today' : ds}.`);
      return;
    }
    setAttendanceDraft((d) => ({ ...d, [ds]: status }));
  };

  const discardAttendanceDraft = () => {
    setAttendanceDraft((d) => {
      const { [today]: _drop, ...rest } = d;
      return rest;
    });
  };

  const addOtToDraft = (e) => {
    e?.preventDefault?.();
    const hours = Number(otForm.hours);
    if (!otForm.date || !(hours > 0)) {
      toast.error('Pick a date and enter OT hours > 0');
      return;
    }
    if (!otForm.reason.trim()) {
      toast.error('Add a reason — tap a chip or type your own');
      return;
    }
    setOtDraft((arr) => ([
      ...arr,
      { tmpId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        date: otForm.date, hours, reason: otForm.reason.trim() },
    ]));
    setOtForm({ date: todayStr(), hours: '', reason: '' });
  };

  const removeOtDraft = (tmpId) =>
    setOtDraft((arr) => arr.filter((o) => o.tmpId !== tmpId));

  const appendReasonChip = (text) => {
    setOtForm((f) => {
      const cur = (f.reason || '').trim();
      return { ...f, reason: cur ? `${cur}; ${text}` : text };
    });
  };

  const pendingCount = Object.keys(attendanceDraft).length + otDraft.length;

  const discardAll = () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`Discard all ${pendingCount} pending change${pendingCount > 1 ? 's' : ''}?`)) return;
    setAttendanceDraft({});
    setOtDraft([]);
    setOtForm({ date: todayStr(), hours: '', reason: '' });
  };

  const saveAll = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    const errors = [];
    try {
      // Run sequentially so a partial failure leaves a clear log
      for (const [date, status] of Object.entries(attendanceDraft)) {
        try {
          await axios.post(
            `${API}/attendance/mark`,
            { date, status },
            { headers: authHeader() },
          );
        } catch (e) {
          errors.push(`Attendance ${date}: ${formatErr(e)}`);
        }
      }
      for (const ot of otDraft) {
        try {
          await axios.post(
            `${API}/overtime`,
            { date: ot.date, hours: ot.hours, notes: ot.reason },
            { headers: authHeader() },
          );
        } catch (e) {
          errors.push(`OT ${ot.date} (${ot.hours}h): ${formatErr(e)}`);
        }
      }
      if (errors.length === 0) {
        toast.success(`Saved ${pendingCount} change${pendingCount > 1 ? 's' : ''}`);
        setAttendanceDraft({});
        setOtDraft([]);
      } else {
        toast.error(`Saved with ${errors.length} error${errors.length > 1 ? 's' : ''}. Check the items in red.`);
        // Leave the failed items in the draft so the user can retry; we
        // don't have per-item identification, but most often everything
        // either succeeds or fails together (auth/CSRF issue).
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  const deletePersistedOt = async (oid) => {
    if (!window.confirm('Delete this saved OT entry?')) return;
    try {
      await axios.delete(`${API}/overtime/${oid}`, { headers: authHeader() });
      toast.success('OT entry removed');
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const downloadMonthlyPdf = async () => {
    setDownloadingPdf(true);
    try {
      const year = cursor.getFullYear();
      const month = cursor.getMonth() + 1;
      const res = await axios.get(
        `${API}/attendance/my-pdf?year=${year}&month=${month}`,
        { headers: authHeader(), responseType: 'blob', timeout: 60000 },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab so mobile browsers show the PDF reader UI; the
      // user can then share / save to Files / WhatsApp from there.
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        // Popup blocked — fall back to anchor download.
        const a = document.createElement('a');
        a.href = url;
        a.download = `Attendance_${year}-${String(month).padStart(2, '0')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Own payslip for the month currently in view — same statement the admin
  // sees on the Payroll page, scoped to the signed-in staff member.
  const downloadPayslipPdf = async () => {
    setDownloadingPayslip(true);
    try {
      const year = cursor.getFullYear();
      const month = cursor.getMonth() + 1;
      const res = await axios.get(
        `${API}/payroll/my-pdf?year=${year}&month=${month}`,
        { headers: authHeader(), responseType: 'blob', timeout: 60000 },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const fileName = `Payslip_${year}-${String(month).padStart(2, '0')}.pdf`;
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDownloadingPayslip(false);
    }
  };

  // Month summary (uses committed data — drafts don't count yet)
  const summary = useMemo(() => {
    const counts = { present: 0, half_day: 0, absent: 0, week_off: 0 };
    attendance.forEach((a) => { if (counts[a.status] !== undefined) counts[a.status] += 1; });
    const otHours = overtime.reduce((sum, o) => sum + Number(o.hours || 0), 0);
    return { ...counts, otHours: Math.round(otHours * 100) / 100 };
  }, [attendance, overtime]);

  return (
    <div className={`p-4 sm:p-6 bg-slate-50 min-h-full space-y-4 ${pendingCount > 0 ? 'pb-28' : ''}`} data-testid="my-attendance-page">
      <div className="flex items-center gap-2">
        <CalendarDays className="text-teal-600" size={22} />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">My Attendance</h1>
      </div>
      <p className="text-sm text-slate-600">
        Mark your own attendance and log overtime with a reason. Changes are
        held as a draft until you click <b>Save changes</b>. {user?.name && (<>Logged in as <b>{user.name}</b>.</>)}
      </p>

      {/* TODAY card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="today-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Today · {today}</div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5">
              {todayDraftStatus ? (
                <span className="inline-flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT[todayDraftStatus] || 'bg-slate-300'}`} />
                  Pending: <b className="uppercase">{todayDraftStatus.replace('_', ' ')}</b>
                  <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">DRAFT</span>
                  <button onClick={discardAttendanceDraft} className="text-[10px] text-slate-500 underline hover:text-slate-800" data-testid="discard-attendance-draft">discard</button>
                </span>
              ) : todayRec ? (
                <span className="inline-flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT[todayRec.status] || 'bg-slate-300'}`} />
                  Marked as <b className="uppercase">{(todayRec.status || '').replace('_', ' ')}</b>
                </span>
              ) : (
                <span className="text-amber-700">Not marked yet</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => {
            const Icon = s.icon;
            const isDraftSelection = todayDraftStatus === s.key;
            return (
              <button
                key={s.key}
                onClick={() => stageAttendance(today, s.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${isDraftSelection ? `${s.color} ring-2 ring-offset-1 ring-slate-700` : `${s.color} hover:opacity-90`}`}
                data-testid={`mark-${s.key}`}
              >
                <Icon size={14} />
                {s.label}
                {isDraftSelection && <span className="text-[9px] opacity-90">·DRAFT</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Month nav + summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100" data-testid="month-prev">←</button>
          <div className="text-sm font-semibold text-slate-900 min-w-[140px] text-center" data-testid="month-label">{monthLabel(cursor)}</div>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100" data-testid="month-next">→</button>
          {/* Jump straight to any past month instead of stepping back one at
              a time — handy when pulling an older payslip. */}
          <input
            type="month"
            value={monthValue(cursor)}
            max={monthValue(new Date())}
            onChange={(e) => {
              const [y, m] = (e.target.value || '').split('-').map(Number);
              if (y && m) setCursor(new Date(y, m - 1, 1));
            }}
            className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
            aria-label="Jump to month"
            title="Jump to any month"
            data-testid="month-jump"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
          <div><span className="text-[10px] uppercase text-slate-400 block">Present</span><span className="text-base font-bold text-emerald-700" data-testid="sum-present">{summary.present}</span></div>
          <div><span className="text-[10px] uppercase text-slate-400 block">Half day</span><span className="text-base font-bold text-amber-700" data-testid="sum-half-day">{summary.half_day}</span></div>
          <div><span className="text-[10px] uppercase text-slate-400 block">Absent</span><span className="text-base font-bold text-red-700" data-testid="sum-absent">{summary.absent}</span></div>
          <div><span className="text-[10px] uppercase text-slate-400 block">Week off</span><span className="text-base font-bold text-slate-500" data-testid="sum-week-off">{summary.week_off}</span></div>
          <div><span className="text-[10px] uppercase text-slate-400 block">OT hours</span><span className="text-base font-bold text-teal-700" data-testid="sum-ot">{summary.otHours}h</span></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={downloadMonthlyPdf}
            disabled={downloadingPdf}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300 text-white font-semibold px-3 py-2 rounded-lg"
            data-testid="download-attendance-pdf"
            title={`Download attendance PDF for ${monthLabel(cursor)}`}
          >
            {downloadingPdf ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />}
            {downloadingPdf ? 'Preparing…' : 'Attendance PDF'}
          </button>
          <button
            type="button"
            onClick={downloadPayslipPdf}
            disabled={downloadingPayslip}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-semibold px-3 py-2 rounded-lg"
            data-testid="download-payslip-pdf"
            title={`Download salary statement for ${monthLabel(cursor)}`}
          >
            {downloadingPayslip ? <Loader2 className="animate-spin" size={14} /> : <Receipt size={14} />}
            {downloadingPayslip ? 'Preparing…' : 'Payslip PDF'}
          </button>
        </div>
      </div>

      {/* Backfill banner */}
      {backfill.active && (
        <div
          className="bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-4 py-3 text-sm flex items-start gap-2"
          data-testid="backfill-banner"
        >
          <AlertCircle size={16} className="mt-0.5 text-amber-600 shrink-0" />
          <div>
            <div className="font-semibold">Backfill window open</div>
            <div className="text-xs mt-0.5">
              You can mark attendance &amp; log OT for any date between{' '}
              <b className="font-mono">{backfill.date_from}</b> and{' '}
              <b className="font-mono">{backfill.date_to}</b>. Tap a date in the calendar to edit it.
            </div>
          </div>
        </div>
      )}

      {/* Calendar grid */}
      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-400 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-center py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((c, idx) => (
            c === null ? (
              <div key={`b-${idx}`} className="h-14" />
            ) : (
              <CalendarCell
                key={c.ds}
                cell={c}
                today={today}
                editable={isDateEditable(c.ds)}
                draftStatus={attendanceDraft[c.ds]}
                onPick={(status) => stageAttendance(c.ds, status)}
              />
            )
          ))}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          P = Present · H = Half day · A = Absent · W = Week off. Tap a P/H/A/W chip on an editable day to mark it. Locked days are greyed out.
        </p>
      </div>

      {/* OT entry + log */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
            <PlusCircle size={14} className="text-teal-600" /> Log overtime
          </h2>
          <form onSubmit={addOtToDraft} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Date</label>
                <input type="date" value={otForm.date}
                  min={backfill.active ? backfill.date_from : todayStr()}
                  max={todayStr()}
                  onChange={(e) => setOtForm({ ...otForm, date: e.target.value })}
                  className={inputCls} data-testid="ot-date" required />
                {backfill.active && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Backfill open: {backfill.date_from} → {backfill.date_to}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Hours</label>
                <input type="number" min="0.25" step="0.25" value={otForm.hours}
                  onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })}
                  className={inputCls} placeholder="e.g. 2.5" data-testid="ot-hours" required />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Reason</label>
              <input value={otForm.reason}
                onChange={(e) => setOtForm({ ...otForm, reason: e.target.value })}
                className={inputCls}
                placeholder="Tap a common reason below or type your own"
                data-testid="ot-reason"
                required />
              {reasonPresets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ot-reason-presets">
                  {reasonPresets.map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => appendReasonChip(p)}
                      className="text-[11px] bg-slate-100 hover:bg-teal-100 hover:text-teal-800 text-slate-600 border border-slate-200 px-2.5 py-1 rounded-full transition"
                      data-testid={`ot-chip-${p.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                    >
                      + {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1.5" data-testid="ot-add-to-draft">
              <PlusCircle size={14} /> Add to draft
            </button>
            <p className="text-[11px] text-slate-500 italic">Items here aren&apos;t saved yet — they queue up below until you hit <b>Save changes</b>.</p>
          </form>

          {/* Pending OT drafts */}
          {otDraft.length > 0 && (
            <div className="mt-4 border-t border-dashed border-amber-300 pt-3">
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-amber-800 mb-2 flex items-center gap-1">
                <AlertCircle size={12} /> Pending OT ({otDraft.length})
              </h3>
              <ul className="space-y-1.5" data-testid="ot-draft-list">
                {otDraft.map((o) => (
                  <li key={o.tmpId} className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-start gap-2" data-testid={`ot-draft-${o.tmpId}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-semibold text-amber-900">{o.date} · {o.hours}h</div>
                      <div className="text-amber-800 truncate">{o.reason}</div>
                    </div>
                    <button onClick={() => removeOtDraft(o.tmpId)} className="text-amber-700 hover:bg-amber-200 rounded p-1" aria-label="Remove" data-testid={`ot-draft-remove-${o.tmpId}`}>
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-900">Saved OT · {monthLabel(cursor)}</h2>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
          ) : overtime.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No OT recorded this month.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="ot-log-list">
              {overtime.map((o) => (
                <li key={o.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50" data-testid={`ot-row-${o.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{o.date} · <span className="text-teal-700">{o.hours}h</span></div>
                    {o.notes && <div className="text-[11px] text-slate-500 truncate" title={o.notes}>{o.notes}</div>}
                  </div>
                  <button onClick={() => deletePersistedOt(o.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`ot-delete-${o.id}`} aria-label="Delete OT entry">
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sticky save-changes bar — only visible when there are pending drafts */}
      {pendingCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-amber-50 border-t-2 border-amber-300 pl-4 pr-28 py-3 z-30 shadow-lg" data-testid="save-bar">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
            <div className="text-sm text-amber-900 flex items-center gap-2 min-w-0">
              <AlertCircle size={16} className="flex-shrink-0" />
              <div className="truncate">
                <b>{pendingCount}</b> pending change{pendingCount > 1 ? 's' : ''} — not saved yet
                {Object.keys(attendanceDraft).length > 0 && <span className="ml-2 text-[11px] bg-amber-200 rounded px-1.5 py-0.5">attendance ×{Object.keys(attendanceDraft).length}</span>}
                {otDraft.length > 0 && <span className="ml-1 text-[11px] bg-amber-200 rounded px-1.5 py-0.5">OT ×{otDraft.length}</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={discardAll} disabled={saving} className="text-xs px-3 py-2 rounded-lg text-slate-700 hover:bg-amber-100 disabled:opacity-50 inline-flex items-center gap-1" data-testid="discard-all">
                <Undo2 size={14} /> Discard
              </button>
              <button onClick={saveAll} disabled={saving} className="text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="save-all">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAttendancePage;

const CELL_BTN_BASE = 'w-5 h-5 flex items-center justify-center text-[9px] font-bold rounded-sm border transition';

const CELL_PICKERS = [
  { key: 'present',  ch: 'P', activeCls: 'bg-emerald-500 text-white border-emerald-600', idleCls: 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200' },
  { key: 'half_day', ch: 'H', activeCls: 'bg-amber-500 text-white border-amber-600',     idleCls: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200' },
  { key: 'absent',   ch: 'A', activeCls: 'bg-red-500 text-white border-red-600',         idleCls: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' },
  { key: 'week_off', ch: 'W', activeCls: 'bg-slate-500 text-white border-slate-600',     idleCls: 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200' },
];

const CalendarCell = ({ cell, today, editable, draftStatus, onPick }) => {
  const isToday = cell.ds === today;
  const savedStatus = cell.rec?.status;
  const effective = draftStatus || savedStatus;
  const hasDraft = !!draftStatus;

  const baseCls = `h-20 rounded border p-1.5 flex flex-col justify-between ${
    isToday ? 'border-teal-500 ring-1 ring-teal-300' : 'border-slate-100'
  } ${editable ? 'bg-white' : 'bg-slate-50/40'}`;

  return (
    <div className={baseCls} data-testid={`cal-cell-${cell.ds}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs font-mono font-bold ${editable ? 'text-slate-800' : 'text-slate-400'}`}>{cell.day}</div>
        {hasDraft && <span className="text-[8px] text-slate-700 font-bold uppercase">DRAFT</span>}
        {!editable && !hasDraft && <span className="text-[10px] text-slate-300" title="Locked — backfill closed">🔒</span>}
      </div>
      {editable ? (
        <div className="flex items-center gap-0.5 justify-around">
          {CELL_PICKERS.map((p) => {
            const isActive = effective === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => onPick(p.key)}
                className={`${CELL_BTN_BASE} ${isActive ? p.activeCls : p.idleCls}`}
                data-testid={`cal-pick-${cell.ds}-${p.key}`}
                title={`${p.ch === 'P' ? 'Present' : p.ch === 'H' ? 'Half day' : p.ch === 'A' ? 'Absent' : 'Week off'}`}
              >
                {p.ch}
              </button>
            );
          })}
        </div>
      ) : (
        effective ? (
          <div className="flex items-center gap-1 opacity-60">
            <span className={`inline-block w-2 h-2 rounded-full ${effective === 'present' ? 'bg-emerald-500' : effective === 'half_day' ? 'bg-amber-500' : effective === 'absent' ? 'bg-red-500' : 'bg-slate-400'}`} />
            <span className="text-[9px] text-slate-500 uppercase truncate">{(effective || '').replace('_', ' ')}</span>
          </div>
        ) : null
      )}
    </div>
  );
};
